import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useYieldCallback } from '@zillow/yield-callback';
import { InView } from 'react-intersection-observer';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import type { ListingDetails } from 'app/types/listingDetails.type';
import RouteActions from 'app/shared/flux/actions/RouteActions';
import { listingUtils_summaryPriceText } from 'app/shared/utils/listingUtils';
import { emitTrackHdpSimilarListingClickEvent } from 'app/shared/models/Clickstream/HdpClickstreamEvents';
import {
  emitTrackClicksIntoPropertyEvent,
  emitTrackImpressionForPropertyCardEvent,
} from 'app/shared/models/Clickstream/ListingCardClickstreamEvents';
import Carousel from '../../../ui/Carousel';
import * as styles from './similarListingsModule.styles';

const getListingPhoto = (listing: ListingDetails): string | null => {
  return listing.photos?.[0]?.url || listing.medPhotoUrls?.[0] || listing.previewPhoto?.url || null;
};

const getListingName = (listing: ListingDetails): string => {
  if (listing.address?.hideStreet) return '(Undisclosed Address)';
  return listing.displayName || listing.address?.street || '(Undisclosed Address)';
};

const getListingPrice = (listing: ListingDetails): string => {
  const summary = listing.pricing?.summary;
  const minMax = listing.listingMinMaxPriceBeds;
  const priceObj =
    summary?.priceLow || summary?.priceHigh ? summary : { priceLow: minMax?.minPrice, priceHigh: minMax?.maxPrice };
  return listingUtils_summaryPriceText(priceObj) || '';
};

export const SimilarListingsModule = () => {
  const dispatch = useDispatch();
  const similarListings = useSelector((state: RootReduxState) => state.fetchListing?.similarListings);

  const breadcrumbs = useSelector(
    (state: RootReduxState) => state.currentListingDetails?.currentListing?.areas?.breadcrumbs,
  );
  const searchSlug = useSelector((state: RootReduxState) => state.filter?.search?.slug) as string | undefined;

  const yieldNavigate = useYieldCallback((path: string) => {
    window.router.transitionTo(path);
  });

  const handleImpressionChange = useCallback((inView: boolean, listing: ListingDetails) => {
    if (inView) {
      emitTrackImpressionForPropertyCardEvent({
        listing,
        triggerLocation: 'similar_listings_module_halo',
      });
    }
  }, []);

  if (!similarListings || similarListings.length === 0) return null;

  const mostSpecificArea = breadcrumbs?.[breadcrumbs.length - 1];
  const areaUri =
    mostSpecificArea?.resourceId && searchSlug
      ? `/${mostSpecificArea.resourceId}/${searchSlug}`
      : (dispatch(RouteActions.getAreaPath()) as unknown as string);

  const handleListingClick = (e: React.MouseEvent<HTMLAnchorElement>, listing: ListingDetails) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
    e.preventDefault();
    emitTrackHdpSimilarListingClickEvent({ triggerLocation: 'home_details_halo' });
    emitTrackClicksIntoPropertyEvent({
      listing,
      triggerLocation: 'similar_listings_module_halo',
    });
    if (listing.uriV2) {
      yieldNavigate(listing.uriV2);
    }
  };

  const handleAreaLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
    e.preventDefault();
    yieldNavigate(areaUri);
  };

  return (
    <div className={styles.container}>
      <Carousel gap="md" snapAlign="start" aria-label="Similar listings">
        {similarListings.map((listing) => {
          const photo = getListingPhoto(listing);
          const name = getListingName(listing);
          const price = getListingPrice(listing);

          return (
            <Carousel.Item key={listing.maloneLotIdEncoded} width="160px">
              <InView onChange={(inView) => handleImpressionChange(inView, listing)} triggerOnce>
                <a
                  href={listing.uriV2 || undefined}
                  onClick={(e) => handleListingClick(e, listing)}
                  className={styles.listingCard}
                >
                  {photo ? (
                    <img src={photo} alt="" className={styles.listingImage} loading="lazy" />
                  ) : (
                    <div className={styles.noPhotoPlaceholder}>No photo</div>
                  )}
                  <h3 className={styles.listingName}>{name}</h3>
                  {price && <span className={styles.listingPrice}>{price}</span>}
                </a>
              </InView>
            </Carousel.Item>
          );
        })}
      </Carousel>

      {mostSpecificArea?.name && (
        <div className={styles.areaLinkContainer}>
          <a href={areaUri} onClick={handleAreaLinkClick} className={styles.areaLink}>
            See more listings in {mostSpecificArea.name}
            <span aria-hidden="true">&rsaquo;</span>
          </a>
        </div>
      )}
    </div>
  );
};
